import React from "react";
import { useApp } from "../context/AppContext";
import { t } from "../lib/i18n";

// 8 short bilingual testimonials. Kept intentionally punchy so multiple fit in
// the marquee before repeat. Names + cities are placeholders — replace when
// real quotes come in.
const QUOTES = [
    {
        id: "t1",
        name: "Marisol R.",
        city: "Miami, FL",
        en: "First app that finally made my money feel calm.",
        es: "La primera app que hizo que mi dinero se sintiera tranquilo.",
    },
    {
        id: "t2",
        name: "Diego A.",
        city: "Austin, TX",
        en: "Caesar caught a subscription I'd been paying for 14 months.",
        es: "Caesar detectó una suscripción que llevaba 14 meses pagando.",
    },
    {
        id: "t3",
        name: "Camila V.",
        city: "Los Angeles, CA",
        en: "The Spanish coaching is the reason I stayed. Feels like family.",
        es: "El coaching en español es la razón por la que me quedé. Se siente familia.",
    },
    {
        id: "t4",
        name: "Javier P.",
        city: "Chicago, IL",
        en: "I finally know what 'safe to spend' actually means for me.",
        es: "Por fin sé qué significa 'puedo gastar' realmente para mí.",
    },
    {
        id: "t5",
        name: "Ana L.",
        city: "Queens, NY",
        en: "Paid off my card 4 months faster with the debt plan.",
        es: "Pagué mi tarjeta 4 meses antes con el plan de deudas.",
    },
    {
        id: "t6",
        name: "Rafael M.",
        city: "Houston, TX",
        en: "Beautiful. Every screen respects my time and my brain.",
        es: "Hermosa. Cada pantalla respeta mi tiempo y mi cabeza.",
    },
    {
        id: "t7",
        name: "Sofía G.",
        city: "Phoenix, AZ",
        en: "Overdraft alert saved me twice already. Worth it alone.",
        es: "La alerta de sobregiro ya me salvó dos veces. Vale la pena solo por eso.",
    },
    {
        id: "t8",
        name: "Luis T.",
        city: "San Diego, CA",
        en: "My wife and I finally talk about money without fighting.",
        es: "Mi esposa y yo por fin hablamos de dinero sin pelear.",
    },
];

const Card = ({ q, lang }) => (
    <figure
        className="w-[300px] sm:w-[340px] shrink-0 rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] px-6 py-5 shadow-sm"
        data-testid={`testimonial-${q.id}`}
    >
        <div className="flex items-center gap-1 text-[#F9C77E]" aria-hidden>
            {"★★★★★".split("").map((_, i) => (
                <svg key={i} width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 17.3 5.8 21l1.6-7L2 9.2l7.1-.6L12 2l2.9 6.6 7.1.6-5.4 4.8 1.6 7z" />
                </svg>
            ))}
        </div>
        <blockquote className="mt-3 text-[15px] leading-relaxed text-[hsl(var(--foreground))]">
            "{lang === "es" ? q.es : q.en}"
        </blockquote>
        <figcaption className="mt-4 flex items-center gap-3">
            <div
                className="w-9 h-9 rounded-full grid place-items-center text-xs font-bold text-white"
                style={{ background: "linear-gradient(135deg, #2E3B5C 0%, #7DD3B0 100%)" }}
            >
                {q.name.split(" ").map((s) => s[0]).join("")}
            </div>
            <div>
                <div className="text-sm font-semibold text-[hsl(var(--foreground))]">{q.name}</div>
                <div className="text-xs text-[hsl(var(--muted-foreground))]">{q.city}</div>
            </div>
        </figcaption>
    </figure>
);

const TestimonialsMarquee = () => {
    const { language } = useApp();
    // Duplicate the list once so the CSS animation can loop seamlessly.
    const doubled = [...QUOTES, ...QUOTES];

    return (
        <section
            className="w-full py-16 md:py-20 bg-[hsl(var(--background))] border-t border-[hsl(var(--border))]"
            data-testid="landing-testimonials"
        >
            <div className="px-5 sm:px-8 md:px-20 lg:px-24 mb-8 md:mb-10 text-center">
                <div className="text-[10px] md:text-xs font-bold tracking-[0.28em] text-[hsl(var(--muted-foreground))] uppercase">
                    {t(language, "testimonials_eyebrow")}
                </div>
                <h2 className="mt-3 font-display font-extrabold text-[hsl(var(--primary))] text-3xl md:text-4xl lg:text-5xl tracking-[-0.02em]">
                    {t(language, "testimonials_title")}
                </h2>
            </div>

            <div
                className="relative overflow-hidden group"
                style={{
                    maskImage: "linear-gradient(90deg, transparent 0, #000 6%, #000 94%, transparent 100%)",
                    WebkitMaskImage: "linear-gradient(90deg, transparent 0, #000 6%, #000 94%, transparent 100%)",
                }}
            >
                <div className="flex gap-5 w-max marquee-track group-hover:[animation-play-state:paused]">
                    {doubled.map((q, i) => (
                        <Card key={`${q.id}-${i}`} q={q} lang={language} />
                    ))}
                </div>
            </div>

            <style>{`
                @keyframes marquee-scroll {
                    from { transform: translateX(0); }
                    to   { transform: translateX(-50%); }
                }
                .marquee-track {
                    animation: marquee-scroll 44s linear infinite;
                    will-change: transform;
                }
                @media (prefers-reduced-motion: reduce) {
                    .marquee-track { animation: none; }
                }
            `}</style>
        </section>
    );
};

export default TestimonialsMarquee;
